import React, { useState, FC, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import HeaderLink, { HeaderLinkProps } from "./HeaderLink";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import window from "global/window";
import { mediaQueries, mediaQueriesMax } from "@src/styles/theme";
import { ChevronDownIcon, BodyText, DetailText, theme } from "@src/components/nessie-web";
import Translate from "@src/components/translation/Translate";
import { HeaderButton } from "@src/components/partials/header/HeaderLink/styles";

interface NavListItemProps extends HeaderLinkProps {
  path: string;
  eventName: string;
  isNarrow?: boolean;
  sublinks?: {
    path: string;
    ctaTranslated: string;
    descriptionTranslated?: string;
    eventName: string;
  }[];
  translationPath?: string;
  highlighted?: boolean;
  onClick?: () => void;
  [key: string]: any;
}

export const ListItem = styled.li`
  list-style: none;
  margin-top: 0;
  display: block;
  ${mediaQueriesMax[2]} {
    width: 100%;
    padding: 10px 15px;
  }
  ${mediaQueries[2]} {
    padding: 10px 8px;
  }
`;

const TransparentButton = styled(HeaderButton)<{ highlighted?: boolean }>`
  display: flex;
  font-weight: 500;
  font-family: "HafferXH", Helvetica, Arial, sans-serif;
  flex-direction: row;
  padding: 10px 0;
  gap: 4px;
  align-items: center;
  background: transparent;
  border: none;
  text-decoration: none;
  cursor: pointer;
  color: #2c2a50;
  &.highlighted {
    color: #7d40ff;
    svg {
      fill: #7d40ff;
    }
  }
  &:hover {
    color: ${theme.colors.contentAccentHover};
    svg {
      fill: ${theme.colors.contentAccentHover};
    }
  }
  ${mediaQueriesMax[2]} {
    padding-block: 0px;
  }
`;

const ResourcesMenu = styled("div")<{ isNarrow?: boolean }>`
  padding: 18px;
  border: 2px solid ${theme.colors.taro30};
  border-radius: 18px;
  a {
    display: block;
    padding: 10px 0px;
    span {
      color: ${theme.colors.taro50};
    }
    &:hover {
      strong {
        color: ${theme.colors.contentAccentHover};
        text-decoration: underline;
      }
    }
  }
  ${mediaQueriesMax[2]} {
    margin-top: 10px;
    padding-block: 8px;
  }
  ${mediaQueries[2]} {
    position: absolute;
    top: 40px;
    width: ${(props) => (props.isNarrow ? "160px" : "360px")};
    left: 0%;
    transform: translate(-20px);
    background: white;
    border: 2px solid ${theme.colors.taro30};
    box-shadow: 0 6px 0 rgba(45, 64, 150, 0.06);
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const NavListItem: FC<NavListItemProps> = ({
  path,
  eventName,
  sublinks,
  translationPath,
  highlighted,
  isNarrow,
  onClick: onClickProp,
  ...linkProps
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onClick = () => {
    if (!window || !window.location) return;

    // Log the event
    logEvent({
      eventValue: window.location.href,
      eventName,
    });

    // Close the menu if onClick prop is provided (for mobile menu)
    if (onClickProp) {
      onClickProp();
    }
  };

  const toggleDropdown = (event: string) => {
    logEvent({
      eventName: event,
      eventValue: window.location.href,
    });
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    // @ts-expect-error Can be `null`
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <ListItem>
      {sublinks ? (
        <DropdownContainer ref={dropdownRef}>
          <TransparentButton className={highlighted ? "highlighted" : ""} onClick={() => toggleDropdown(eventName)}>
            <Translate path={translationPath} />
            <ChevronDownIcon size="xs" />
          </TransparentButton>
          {isDropdownOpen && (
            <ResourcesMenu isNarrow={isNarrow}>
              {sublinks.map((link, index) => (
                <HeaderLink
                  key={index}
                  to={getRelativePath(link.path)}
                  onClick={() => {
                    toggleDropdown(link.eventName);
                    // Close the menu if onClick prop is provided (for mobile menu)
                    if (onClickProp) {
                      onClickProp();
                    }
                  }}
                >
                  <>
                    <BodyText as="strong" css={{ fontSize: "16px" }}>
                      <Translate path={link.ctaTranslated} />
                    </BodyText>
                    {link.descriptionTranslated && (
                      <DetailText css={{ fontSize: "16px" }}>
                        <Translate path={link.descriptionTranslated} />
                      </DetailText>
                    )}
                  </>
                </HeaderLink>
              ))}
            </ResourcesMenu>
          )}
        </DropdownContainer>
      ) : (
        <HeaderLink to={getRelativePath(path)} onClick={onClick} className={linkProps.className}>
          <Translate path={translationPath} />
        </HeaderLink>
      )}
    </ListItem>
  );
};

export default NavListItem;
